import React, { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useAlarms } from "./useAlarms";
import { activeScreens, isLegacyScreen } from "./boardSchema";
import BoardRenderer from "./BoardRenderer";
import AlarmMapWidget from "./widgets/AlarmMapWidget";
import { playAlarmTone } from "../utils/alarmTone";
import "./board.scss";

// New per-board renderer with alarm takeover. When an alarm is active and the
// board opts into takeover, incidents pre-empt the normal screen rotation
// (mirrors the legacy board's behavior), then it returns to rotation when clear.
const MachineBoard = () => {
  const { boardConfig, agency } = useAuth();
  const alarms = useAlarms(agency);
  const takeover = boardConfig?.alarmTakeover;
  const active = takeover?.enabled !== false && takeover?.mode !== "off" && alarms.length > 0;

  // Alarm tone on a new incident. Plays unattended in the desktop app or a
  // kiosk browser that allows autoplay; elsewhere the browser blocks it
  // quietly (see utils/alarmTone.js).
  const prevCount = useRef(0);
  useEffect(() => {
    if (active && takeover?.audible && alarms.length > prevCount.current) {
      playAlarmTone(`${process.env.PUBLIC_URL || ""}/fire_emergency.mp3`);
    }
    prevCount.current = alarms.length;
  }, [active, alarms.length, takeover]);

  // A board with a legacy screen (the Legacy template) takes over the way the
  // legacy board always has: that screen switches to its own alarm map.
  const legacyScreen = activeScreens(boardConfig).find(isLegacyScreen);
  if (active && legacyScreen) {
    return <BoardRenderer forceScreenId={legacyScreen.id} />;
  }

  // A board saved without alarmTakeover settings still takes over (full-screen
  // map), so `takeover` may be undefined here.
  if (active && takeover?.mode === "dedicatedScreen" && takeover?.screenId) {
    return <BoardRenderer forceScreenId={takeover.screenId} />;
  }

  if (active) {
    // Full-screen incident map takeover.
    return (
      <div className="sb-takeover" style={{ background: boardConfig?.theme?.background || "#0b0b0d" }}>
        <AlarmMapWidget config={{}} agency={agency} board={boardConfig} />
      </div>
    );
  }

  return <BoardRenderer />;
};

export default MachineBoard;
